import React, { useEffect, useState, useRef } from "react";
import MainLayout from "../../Layout/mainLayout";
import { ReactComponent as Add } from "../../Global Icons/adminIcon.svg";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import AddImage from "../../Global Functions/addImage";
import { viewCountry, viewCountryListPagination } from "../../Redux/Actions/settingsActions";
import { addAdmin } from "../../Redux/Actions/adminActions";
import { getEnums } from "../../Redux/Actions/enumsActions";
import { ScrollTop, ToFirstCapital } from "../../Utils/helpers";
import { FaUser, FaEnvelope, FaPhone, FaBuilding, FaLock } from "react-icons/fa";
import "../Reward Voucher/addReward.css";

const AddAdmins = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formState, setFormState] = useState({
    name: "",
    email: "",
    country_code: "",
    contact_number: "",
    role: "admin",
    timezone: "",
    department: "",
    profile_image: "",
    designation: "",
    pwd: "",
    address: "",
  });

  const [selectedCountry, setSelectedCountry] = useState("");
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { countryList, country } = useSelector((state) => state.country || {});
  const { enumsData } = useSelector((state) => state.enumsData || {});

  // Effects
  useEffect(() => {
    dispatch(viewCountryListPagination());
    dispatch(getEnums());
    ScrollTop();
  }, [dispatch]);

  useEffect(() => {
    if (selectedCountry) {
      dispatch(viewCountry(selectedCountry));
    }
  }, [selectedCountry, dispatch]);

  useEffect(() => {
    if (country?.data?.country_phone_code) {
      setFormState(prev => ({ ...prev, country_code: country.data.country_phone_code }));
    }
  }, [country]);

  useEffect(() => {
    const timeZones = enumsData?.data?.time_zones
      ? Object.entries(enumsData.data.time_zones)
        .filter(([countryName]) => countryName === country?.data?.country_name)
        .map(([countryName, tzObj]) => ({
          label: `${tzObj.timezone} (UTC${tzObj.offset_UTC})`,
          value: tzObj.timezone,
        }))
      : [];
    setTimezoneOptions(timeZones);
  }, [enumsData, country]);

  const departments = enumsData?.data?.department
    ? Object.values(enumsData.data.department)
    : [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const url = await AddImage(file);
      if (url) {
        setFormState(prev => ({ ...prev, profile_image: url }));
        toast.success("Profile image uploaded successfully!");
      }
    } catch {
      toast.error("Image upload failed");
    }
  };

  const validateForm = () => {
    if (!formState.name.trim()) return "Name is required";
    if (!formState.email.trim()) return "Email is required";
    if (!/^\S+@\S+\.\S+$/.test(formState.email)) return "Invalid email format";
    if (formState.contact_number.length !== 10) return "Contact number must be 10 digits";
    if (!selectedCountry) return "Please select a country";
    if (formState.pwd.length < 8) return "Password must be at least 8 characters";
    if (!formState.designation.trim()) return "Designation is required";
    if (!formState.department) return "Department is required";
    return null;
  };

  const handleSubmit = async () => {
    const error = validateForm();
    if (error) {
      toast.error(error);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await dispatch(addAdmin(formState));
      if (result.statusCode === 200) {
        toast.success("Admin added successfully!");
        navigate("/manage_admins");
      }
    } catch (error) {
      toast.error("Failed to add admin");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="franchise-form-container">
        <div className="franchise-form-wrapper franchise-card">
          <div className="franchise-card-content">
            {/* Header */}
            <div className="franchise-header" style={{ display: "flex", gap: "25px"}}>
              <div className="franchise-header-icon">
                <Add />
              </div>
              <h1 style={{ marginTop: "15px"}}>Add New Admin</h1>
            </div>

            {/* Profile Image Upload */}
            <div className="form-section">
              <label className="form-label file-upload-area" htmlFor="profile_image">
                <FaUser style={{ fontSize: "2rem", marginBottom: "0.5rem" }} />
                <div>Profile Photo</div>
                <div className="file-upload-text">
                  Click to upload or drag and drop (JPG, PNG)
                </div>
              </label>
              <input
                ref={fileInputRef}
                id="profile_image"
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                onChange={handleImageUpload}
                className="form-input"
                style={{ display: "none" }}
              />
              {formState.profile_image && (
                <div style={{ textAlign: "center", marginTop: "1rem" }}>
                  <img
                    src={formState.profile_image}
                    alt="Profile preview"
                    style={{
                      width: "100px",
                      height: "100px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "4px solid #7c3aed",
                    }}
                  />
                </div>
              )}
            </div>

            {/* 2-Column Grid */}
            <div className="form-grid-2">
              {/* Name & Email */}
              <div className="form-section">
                <label className="form-label">Full Name <span style={{ color: "red" }}>*</span></label>
                <div className="input-icon-container">
                  <FaUser className="input-icon" />
                  <input
                    name="name"
                    value={formState.name}
                    onChange={handleChange}
                    className="form-input with-icon"
                    placeholder="Enter full name"
                    required
                  />
                </div>
              </div>

              <div className="form-section">
                <label className="form-label">Email <span style={{ color: "red" }}>*</span></label>
                <div className="input-icon-container">
                  <FaEnvelope className="input-icon" />
                  <input
                    name="email"
                    type="email"
                    value={formState.email}
                    onChange={handleChange}
                    className="form-input with-icon"
                    placeholder="admin@company.com"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Contact & Country */}
            <div className="form-grid-2">
              <div className="form-section">
                <label className="form-label">Contact Number <span style={{ color: "red" }}>*</span></label>
                <div className="input-icon-container">
                  <FaPhone className="input-icon" style={{ top: "35%"}} />
                  <input
                    name="contact_number"
                    type="tel"
                    value={formState.contact_number}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormState(prev => ({ ...prev, contact_number: value }));
                    }}
                    maxLength={10}
                    className="form-input with-icon"
                    placeholder="10 digit number"
                  />
                  <div style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: "0.25rem" }}>
                    {formState.contact_number.length}/10
                  </div>
                </div>
              </div>

              <div className="form-section">
                <label className="form-label">Country <span style={{ color: "red" }}>*</span></label>
                <select
                  name="country"
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className="form-input"
                >
                  <option value="">Select Country</option>
                  {countryList?.data?.map((country) => (
                    <option key={country._id} value={country._id}>
                      {country.country_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Timezone & Department */}
            <div className="form-grid-2">
              <div className="form-section">
                <label className="form-label">Time Zone</label>
                <select
                  name="timezone"
                  value={formState.timezone}
                  onChange={handleChange}
                  disabled={!selectedCountry}
                  className="form-input"
                >
                  <option value="">Select Time Zone</option>
                  {timezoneOptions.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-section">
                <label className="form-label">Department <span style={{ color: "red" }}>*</span></label>
                <select
                  name="department"
                  value={formState.department}
                  onChange={handleChange}
                  className="form-input"
                  required
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>
                      {ToFirstCapital(dept)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Designation & Password */}
            <div className="form-grid-2">
              <div className="form-section">
                <label className="form-label">Designation <span style={{ color: "red" }}>*</span></label>
                <div className="input-icon-container">
                  <FaBuilding className="input-icon" />
                  <input
                    name="designation"
                    value={formState.designation}
                    onChange={handleChange}
                    className="form-input with-icon"
                    placeholder="e.g. Software Engineer"
                    required
                  />
                </div>
              </div>

              <div className="form-section">
                <label className="form-label">Password <span style={{ color: "red" }}>*</span></label>
                <div className="input-icon-container">
                  <FaLock className="input-icon" />
                  <input
                    name="pwd"
                    type={showPassword ? "text" : "password"}
                    value={formState.pwd}
                    onChange={handleChange}
                    className="form-input with-icon"
                    placeholder="Minimum 8 characters"
                    minLength={8}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                      </svg>
                    ) : (
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M2 4.25L5.75 8c.17-.08.34-.15.53-.21C7.04 7.45 7.58 7.25 8.14 7.25c1.22 0 2.3.52 3.09 1.35.35.35.69.72 1.02 1.11l2.17-2.17c.04-.04.07-.08.11-.12l4.25 4.25c-.67.89-1.26 1.88-1.77 2.95-.02.04-.04.08-.06.12L18 15.75l-1.77 1.77c-.04.04-.08.07-.12.11-.29-.39-.6-.76- .92-1.11-.79-.83-1.79-1.35-2.88-1.35-.56 0-1.1.2-1.59.54-.07.06-.14.11-.21.17L11 17.25l-2.17-2.17c-.39-.39-.76-.6-1.11-.92-.83-.79-1.35-1.79-1.35-2.88 0-.56.2-1.1.54-1.59.06-.07.11-.14.17-.21L2 4.25zm13.09 6.09c.39.39.76.6 1.11.92.83.79 1.35 1.79 1.35 2.88 0 .56-.2 1.1-.54 1.59-.06.07-.11.14-.17.21L15.09 19.75l-2.17-2.17c-.39-.39-.76-.6-1.11-.92-.79-.83-1.35-1.79-1.35-2.88 0-.56.2-1.1.54-1.59.06-.07.11-.14.17-.21L13.09 10.34z" />
                      </svg>
                    )}
                  </button>
                </div>
                <div style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: "0.25rem" }}>
                  {formState.pwd.length >= 8 ? "✓ Strong" : `${formState.pwd.length}/8`}
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="form-navigation">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate("/manage_admins")}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn  ${isSubmitting ? 'loading' : ''}`}
                onClick={handleSubmit}
                disabled={isSubmitting}
                style={{ boxShadow: "none"}}
              >
                {isSubmitting ? "Adding..." : "Add Admin"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AddAdmins;
